import type { ComponentProps } from "react";
import Link from "next/link";

import {
  Button,
  type ButtonVariantProps,
} from "@tripcart/traveler-ui/components/button";

/**
 * A link that looks like a button.
 *
 * Use this instead of `<Button render={<Link/>}>label</Button>` wherever the
 * result is a navigation, which on this storefront is nearly everywhere.
 *
 * The dashboard kit's `<Button>` was a client component, so a server component
 * passing `<Link>` as its child sent that child across the RSC boundary. During
 * SSR the child did not satisfy `React.isValidElement`, `asChild` silently did
 * nothing, and the server emitted `<button type="button">` where the client
 * emitted `<a href>`. React then discarded the subtree and rebuilt it:
 *
 *   Hydration failed because the server rendered HTML didn't match the client.
 *
 * Confirmed live on `/claim/[token]` and `/bookings/[id]`, and it applied to
 * every server-rendered call site — the home hero, the nav menu and the tours
 * index among them, which is most of the storefront's first paint.
 *
 * There is no conditional here: `Button` clones the `<Link>` it is handed as
 * `render` straight onto an anchor, with no client primitive in between, so
 * both renders agree by construction.
 */
export function ButtonLink({
  variant = "default",
  tone = "default",
  size = "default",
  className,
  children,
  ...props
}: ComponentProps<typeof Link> & ButtonVariantProps) {
  return (
    <Button
      variant={variant}
      tone={tone}
      size={size}
      className={className}
      render={<Link {...props} />}
    >
      {children}
    </Button>
  );
}
